import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MUSCLE_OPTIONS = [
  { value: '', label: '— sin etiquetar —' },
  { value: 'gluteo', label: 'Glúteo' },
  { value: 'isquios', label: 'Isquios / Femorales' },
  { value: 'cuadriceps', label: 'Cuádriceps' },
  { value: 'aductores', label: 'Aductores' },
  { value: 'abductores', label: 'Abductores' },
  { value: 'pantorrilla', label: 'Pantorrilla' },
  { value: 'espalda', label: 'Espalda' },
  { value: 'hombros', label: 'Hombros' },
  { value: 'pecho', label: 'Pecho' },
  { value: 'biceps', label: 'Bíceps' },
  { value: 'triceps', label: 'Tríceps' },
  { value: 'lumbar', label: 'Lumbar' },
  { value: 'core', label: 'Core / Abdomen' },
  { value: 'cardio', label: 'Cardio' },
];

const TYPE_OPTIONS = [
  { value: '', label: '— sin etiquetar —' },
  { value: 'activador', label: '🔥 Activador' },
  { value: 'principal', label: '⭐ Principal' },
  { value: 'secundario', label: '➕ Secundario' },
  { value: 'complementario', label: '🎯 Complementario' },
];

const SEX_OPTIONS = [
  { value: 'ambos', label: '👥 Ambos' },
  { value: 'mujer', label: '🌸 Mujer' },
  { value: 'hombre', label: '💪 Hombre' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: '', label: '🏋️ Ejercicio (default)' },
  { value: 'ejercicio', label: '🏋️ Ejercicio' },
  { value: 'tecnica', label: '🔥 Técnica de entrenamiento' },
  { value: 'metodo', label: '📚 Método / Educativo' },
];

const LOCATION_OPTIONS = [
  { value: '', label: '🏠🏋️ Ambos (default)' },
  { value: 'ambos', label: '🏠🏋️ Ambos' },
  { value: 'gym', label: '🏋️ Solo Gym' },
  { value: 'casa', label: '🏠 Solo Casa' },
];

export default function BulkTagEditor({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // all | untagged | tagged | mandatory
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/videos/suggest-tags`, {});
      // Cada fila arranca con los valores actuales, y `suggested_muscle_group` como sugerencia visible
      setRows((data.suggestions || []).map((s) => ({
        id: s.id,
        title: s.title,
        exercise_type: s.current_exercise_type || '',
        muscle_group: s.current_muscle_group || '',
        recommended_sex: s.current_recommended_sex || 'ambos',
        content_type: s.current_content_type || '',
        location: s.current_location || '',
        is_mandatory: !!s.current_is_mandatory,
        suggested_muscle_group: s.suggested_muscle_group || '',
        dirty: false,
      })));
    } catch (e) {
      toast.error('Error cargando videos');
    } finally {
      setLoading(false);
    }
  };

  const applySuggestions = () => {
    setRows((prev) => prev.map((r) => {
      if (r.muscle_group) return r; // no sobreescribir si ya tiene
      if (!r.suggested_muscle_group) return r;
      return { ...r, muscle_group: r.suggested_muscle_group, dirty: true };
    }));
    toast.success('Sugerencias aplicadas · revisa y guarda');
  };

  const setDefaultsForRow = (id, patch) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch, dirty: true } : r));
  };

  const bulkSetType = (val) => {
    setRows((prev) => prev.map((r) => filteredIds.has(r.id) ? { ...r, exercise_type: val, dirty: true } : r));
  };
  const bulkSetSex = (val) => {
    setRows((prev) => prev.map((r) => filteredIds.has(r.id) ? { ...r, recommended_sex: val, dirty: true } : r));
  };
  const bulkSetLocation = (val) => {
    setRows((prev) => prev.map((r) => filteredIds.has(r.id) ? { ...r, location: val, dirty: true } : r));
  };
  const bulkSetMandatory = (val) => {
    setRows((prev) => prev.map((r) => filteredIds.has(r.id) ? { ...r, is_mandatory: val, dirty: true } : r));
  };

  const save = async () => {
    const dirty = rows.filter((r) => r.dirty);
    if (dirty.length === 0) { toast.info('Nada que guardar'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/videos/bulk-tags`, {
        updates: dirty.map((r) => ({
          id: r.id,
          exercise_type: r.exercise_type,
          muscle_group: r.muscle_group,
          recommended_sex: r.recommended_sex,
          content_type: r.content_type,
          location: r.location,
          is_mandatory: !!r.is_mandatory,
        })),
      });
      toast.success(`✅ ${data.updated} videos actualizados`);
      setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
    } catch (e) {
      toast.error('Error guardando cambios: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    let list = rows;
    if (filter === 'untagged') list = list.filter((r) => !r.muscle_group || !r.exercise_type);
    else if (filter === 'tagged') list = list.filter((r) => r.muscle_group && r.exercise_type);
    else if (filter === 'mandatory') list = list.filter((r) => !!r.is_mandatory);
    if (q) list = list.filter((r) => (r.title || '').toLowerCase().includes(q));
    return list;
  }, [rows, filter, searchQuery]);

  const filteredIds = useMemo(() => new Set(filtered.map((r) => r.id)), [filtered]);

  const dirtyCount = rows.filter((r) => r.dirty).length;
  const untaggedCount = rows.filter((r) => !r.muscle_group || !r.exercise_type).length;
  const mandatoryCount = rows.filter((r) => !!r.is_mandatory).length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="bulk-tag-editor">
      <div className="bg-slate-900 border border-pink-500/40 rounded-2xl max-w-7xl w-full my-6 p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-black text-white">🏷️ Etiquetado masivo de videos</h2>
            <p className="text-sm text-gray-400 mt-1">
              Etiqueta muchos videos a la vez · <span className="text-amber-300 font-semibold">{untaggedCount}</span> sin etiquetar · <span className="text-emerald-300 font-semibold">{dirtyCount}</span> cambios pendientes
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {rows.length === 0 && (
              <Button data-testid="bulk-load" onClick={load} disabled={loading} className="bg-pink-600 hover:bg-pink-700">
                {loading ? 'Cargando…' : '📥 Cargar videos'}
              </Button>
            )}
            {rows.length > 0 && (
              <>
                <Button data-testid="bulk-suggest" onClick={applySuggestions} variant="outline" className="border-cyan-400 text-cyan-300 hover:bg-cyan-500/20">
                  ⚡ Aplicar sugerencias
                </Button>
                <Button data-testid="bulk-save" onClick={save} disabled={saving || dirtyCount === 0} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                  {saving ? 'Guardando…' : `💾 Guardar (${dirtyCount})`}
                </Button>
              </>
            )}
            <Button onClick={onClose} variant="ghost" className="text-gray-400 hover:text-white">✕ Cerrar</Button>
          </div>
        </div>

        {rows.length > 0 && (
          <>
            {/* Toolbar de filtros y acciones masivas */}
            <div className="flex items-center gap-3 flex-wrap mb-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700">
              <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                <span className="text-xs font-bold text-gray-400 uppercase">🔍 Buscar:</span>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="prensa, sentadilla, hip thrust…"
                  data-testid="bulk-search-input"
                  className="flex-1 px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm border border-slate-600 focus:border-pink-500 focus:outline-none"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} data-testid="bulk-search-clear" className="text-xs text-gray-400 hover:text-white px-2">✕</button>
                )}
              </div>
              <div className="w-full h-px bg-slate-700 my-1" />
              <span className="text-xs font-bold text-gray-400 uppercase">Filtrar:</span>
              {[['all', `Todos (${rows.length})`], ['untagged', `Sin etiquetar (${untaggedCount})`], ['tagged', 'Ya etiquetados'], ['mandatory', `⭐ Obligatorios (${mandatoryCount})`]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`} className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${filter === k ? 'bg-pink-500 text-white' : 'bg-slate-700 text-gray-300 hover:bg-slate-600'}`}>{l}</button>
              ))}
              <div className="w-px h-6 bg-slate-600 mx-2" />
              <span className="text-xs font-bold text-gray-400 uppercase">Aplicar a lista visible:</span>
              <select onChange={(e) => { if (e.target.value) { bulkSetType(e.target.value); e.target.value = ''; } }} className="px-2 py-1 rounded bg-slate-700 text-white text-xs" data-testid="bulk-set-type">
                <option value="">Tipo…</option>
                {TYPE_OPTIONS.filter(o => o.value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select onChange={(e) => { if (e.target.value) { bulkSetSex(e.target.value); e.target.value = ''; } }} className="px-2 py-1 rounded bg-slate-700 text-white text-xs" data-testid="bulk-set-sex">
                <option value="">Sexo…</option>
                {SEX_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select onChange={(e) => { if (e.target.value) { bulkSetLocation(e.target.value); e.target.value = ''; } }} className="px-2 py-1 rounded bg-slate-700 text-white text-xs" data-testid="bulk-set-location">
                <option value="">Lugar…</option>
                {LOCATION_OPTIONS.filter(o => o.value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                onClick={() => bulkSetMandatory(true)}
                data-testid="bulk-set-mandatory-on"
                className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
                title="Marcar como Obligatorios (Siempre incluir) la lista visible"
              >⭐ Obligatorio ✓</button>
              <button
                onClick={() => bulkSetMandatory(false)}
                data-testid="bulk-set-mandatory-off"
                className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold"
                title="Quitar Obligatorio de la lista visible"
              >⭐ Obligatorio ✕</button>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto rounded-lg border border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-gray-300">
                  <tr>
                    <th className="text-left px-3 py-2">Video</th>
                    <th className="text-left px-3 py-2 w-40">Contenido</th>
                    <th className="text-left px-3 py-2 w-40">Tipo</th>
                    <th className="text-left px-3 py-2 w-48">Grupo muscular</th>
                    <th className="text-left px-3 py-2 w-32">Sexo</th>
                    <th className="text-left px-3 py-2 w-36">Lugar</th>
                    <th className="text-center px-3 py-2 w-24">⭐ Oblig.</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className={`border-t border-slate-700 ${r.dirty ? 'bg-emerald-900/20' : ''} ${r.is_mandatory ? 'ring-1 ring-amber-500/40' : ''}`} data-testid={`bulk-row-${r.id}`}>
                      <td className="px-3 py-2 text-white">
                        <div className="font-semibold text-sm">{r.title}</div>
                        {r.suggested_muscle_group && r.suggested_muscle_group !== r.muscle_group && (
                          <div className="text-xs text-cyan-400 mt-0.5">
                            💡 Sugerido: <b>{r.suggested_muscle_group}</b>
                            <button
                              onClick={() => setDefaultsForRow(r.id, { muscle_group: r.suggested_muscle_group })}
                              className="ml-2 underline hover:text-cyan-200"
                              data-testid={`apply-suggestion-${r.id}`}
                            >
                              aplicar
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <select value={r.content_type || ''} onChange={(e) => setDefaultsForRow(r.id, { content_type: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 text-white text-xs border border-slate-600" data-testid={`content-type-${r.id}`}>
                          {CONTENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={r.exercise_type} onChange={(e) => setDefaultsForRow(r.id, { exercise_type: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 text-white text-xs border border-slate-600">
                          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={r.muscle_group} onChange={(e) => setDefaultsForRow(r.id, { muscle_group: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 text-white text-xs border border-slate-600">
                          {MUSCLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={r.recommended_sex} onChange={(e) => setDefaultsForRow(r.id, { recommended_sex: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 text-white text-xs border border-slate-600">
                          {SEX_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={r.location || ''} onChange={(e) => setDefaultsForRow(r.id, { location: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 text-white text-xs border border-slate-600" data-testid={`location-${r.id}`}>
                          {LOCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer" title="Siempre incluir este ejercicio cuando el músculo se entrene">
                          <input
                            type="checkbox"
                            checked={!!r.is_mandatory}
                            onChange={(e) => setDefaultsForRow(r.id, { is_mandatory: e.target.checked })}
                            className="w-4 h-4 accent-amber-500"
                            data-testid={`mandatory-${r.id}`}
                          />
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
